import React, { useCallback, useEffect, useState } from 'react';
import { format } from 'date-fns';
import {
  Box,
  Typography,
  IconButton,
  Collapse,
  CircularProgress,
  Chip,
  Stack,
  Button,
  Link,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import {
  fetchMembersBySearch,
  isMemberRecordActive,
  pickMemberResultsForEmail,
} from './memberSearchApi';
import { capDisplayValue } from './capDisplayValue';

const OXR_CAP_PARENTS = new Set(['OXR DTC', 'ONX MAPS']);

const formatMembershipDate = (value) => {
  if (!value) return '—';
  try {
    return format(new Date(value), 'MMM d, yyyy');
  } catch {
    return value;
  }
};

const MemberMatchRow = ({ record, onOpenCapDrawer }) => {
  const active = isMemberRecordActive(record);
  const membershipExt = capDisplayValue(record.MembershipEXT);
  const membershipInt = capDisplayValue(record.MembershipINT);
  const showCapLink = OXR_CAP_PARENTS.has(record.Parent) && membershipExt && onOpenCapDrawer;

  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 1,
        border: '1px solid #e0e0e0',
        bgcolor: '#fff',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="body2" fontWeight={600}>
          {[record.First, record.Last].filter(Boolean).join(' ') || '—'}
        </Typography>
        <Chip
          size="small"
          label={active ? 'Active' : 'Inactive'}
          color={active ? 'success' : 'default'}
          variant={active ? 'filled' : 'outlined'}
        />
      </Box>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
        {[record.Parent, record.Type].filter(Boolean).join(' · ') || '—'}
      </Typography>
      <Stack spacing={0.25} sx={{ mt: 1 }}>
        {membershipExt && (
          <Typography variant="body2">
            <Typography component="span" variant="caption" color="text.secondary">Client ID </Typography>
            {membershipExt}
          </Typography>
        )}
        {membershipInt && (
          <Typography variant="body2">
            <Typography component="span" variant="caption" color="text.secondary">FP ID </Typography>
            {membershipInt}
          </Typography>
        )}
        {record.Phone && (
          <Typography variant="body2">
            <Typography component="span" variant="caption" color="text.secondary">Phone </Typography>
            {record.Phone}
          </Typography>
        )}
        <Typography variant="body2">
          <Typography component="span" variant="caption" color="text.secondary">Term </Typography>
          {formatMembershipDate(record.Start)} – {formatMembershipDate(record.End)}
        </Typography>
        {record.Company && (
          <Typography variant="body2">
            <Typography component="span" variant="caption" color="text.secondary">Division </Typography>
            {record.Company}
          </Typography>
        )}
      </Stack>
      {showCapLink && (
        <Button
          size="small"
          sx={{ mt: 1, p: 0, minWidth: 0, textTransform: 'none' }}
          onClick={() => onOpenCapDrawer(record)}
        >
          Contacts & devices
        </Button>
      )}
    </Box>
  );
};

const NpsMemberEmailLookup = ({ email, onOpenCapDrawer }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [records, setRecords] = useState(null);

  const load = useCallback(async () => {
    if (!email) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchMembersBySearch(email);
      setRecords(pickMemberResultsForEmail(data, email));
    } catch (err) {
      setRecords(null);
      setError(err.message || 'Lookup failed');
    } finally {
      setLoading(false);
    }
  }, [email]);

  useEffect(() => {
    setOpen(false);
    setRecords(null);
    setError(null);
    setLoading(false);
  }, [email]);

  const handleToggle = () => {
    if (!email) return;
    if (open) {
      setOpen(false);
      return;
    }
    setOpen(true);
    if (records === null && !loading) {
      load();
    }
  };

  if (!email) return null;

  return (
    <Box sx={{ mt: 0.25 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
        <EmailOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
        <Typography variant="body2" color="text.secondary">{email}</Typography>
        <IconButton
          size="small"
          onClick={handleToggle}
          aria-expanded={open}
          aria-label={open ? 'Hide member lookup' : 'Look up member by email'}
          title={open ? 'Hide member lookup' : 'Look up member in Operations Hub'}
          sx={{
            ml: 0.25,
            color: open ? 'primary.main' : 'text.secondary',
            bgcolor: open ? 'action.selected' : 'transparent',
          }}
        >
          <SearchIcon sx={{ fontSize: 18 }} />
        </IconButton>
        {open && records !== null && !loading && !error && (
          <Typography variant="caption" color="text.secondary">
            {records.length === 0
              ? 'No memberships found'
              : `${records.length} membership${records.length === 1 ? '' : 's'}`}
          </Typography>
        )}
      </Box>

      <Collapse in={open}>
        <Box
          sx={{
            mt: 1,
            p: 1.5,
            borderRadius: 1.5,
            bgcolor: '#f0f4f8',
            border: '1px solid #cfd8dc',
          }}
        >
          {loading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">Searching…</Typography>
            </Box>
          )}

          {error && !loading && (
            <Box>
              <Typography variant="body2" color="error" sx={{ mb: 1 }}>{error}</Typography>
              <Link component="button" variant="body2" onClick={load} sx={{ cursor: 'pointer' }}>
                Try again
              </Link>
            </Box>
          )}

          {!loading && !error && records !== null && records.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No member records match this email in Operations Hub.
            </Typography>
          )}

          {!loading && !error && records && records.length > 0 && (
            <Stack spacing={1}>
              {records.map((record, index) => (
                <MemberMatchRow
                  key={`${record.MembershipINT || record.MembershipEXT || record.Email}-${index}`}
                  record={record}
                  onOpenCapDrawer={onOpenCapDrawer}
                />
              ))}
            </Stack>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default NpsMemberEmailLookup;
